import { StyleSheet } from '@react-pdf/renderer';
import { isDualShareCompactLayout, isValueFocusCompactLayout } from '../dualShareCompactLayout';
import { isPvmGpsTemplate } from '../../69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import { PVM_HEADER_PADDING_BELOW_TOP_BORDER_PDF, PVM_HEADER_RIGHT_COLUMN_WIDTH_PDF } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmHeader';
import { PVM_HEADLINE_COLOR, PVM_HEADER_TITLE_FONT_SIZE_PDF, PVM_TEXT_COLOR, PVM_HEADLINE_FONT, PVM_BODY_FONT } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmBranding';

// PDF styles for react-pdf renderer
// Matching the backend pdfDocDefinition structure for template 630cebfecb9741ec6761744d

/** Backend v1 `colors.gray` — share-class suffix after fund title (`title_addition`). */
export const PDF_HEADER_TITLE_ADDITION_COLOR = '#6D6E70';

export const pdfStyles = StyleSheet.create({
  page: {
    padding: '16px 20px 16px 20px',
    fontFamily: 'Calibri',
    fontSize: 8.5,
    flexDirection: 'column',
  },
  /** Page 1 body below intro: single flex child expands so left/right columns can stretch to page bottom */
  page1VerticalFill: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  /** ¹/² notes pinned to bottom of the right column */
  page1ChartFootnotesInColumn: {
    width: '100%',
    paddingTop: 6,
    marginTop: 'auto',
    flexShrink: 0,
  },
  page1ChartFootnotesText: {
    fontSize: 6,
    lineHeight: 1.8,
    textAlign: 'right',
  },
  header: {
    marginBottom: 0,
  },
  headerTopText: {
    fontSize: 8,
    color: '#949599',
    fontWeight: 700,
    marginBottom: 0,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 4,
  },
  headerSplitter: {
    height: '0.5px',
    backgroundColor: '#6d6e70',
    width: '83.2%',
  },
  headerLeftSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerRightSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerLogoSection: {
    width: 45,
    marginRight: 16,
    position: 'relative',
    marginTop: 5,
  },
  headerLogoRect1: {
    width: 50,
    height: 18,
    backgroundColor: '#003f7a',
    marginBottom: 2.5,
  },
  headerLogoRect2: {
    width: 50,
    height: 54,
    backgroundColor: '#003f7a',
    position: 'relative',
  },
  headerLogoImage: {
    width: 36,
    height: 36,
    position: 'absolute',
    top: 8,
    left: 8,
  },
  headerTitleSection: {
    width: 300,
    marginTop: 10,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  headerTitleText: {
    fontSize: 11,
    color: '#6D6E70',
    fontWeight: 700,
  },
  headerDateText: {
    fontSize: 11,
    color: '#949599',
    fontWeight: 700,
  },
  headerMainTitle: {
    fontSize: 24,
    color: '#003f7a',
    fontWeight: 700,
    lineHeight: 0.9,
    marginTop: 2,
    width: 250,
  },
  headerTitleAddition: {
    fontSize: 24,
    color: PDF_HEADER_TITLE_ADDITION_COLOR,
    fontWeight: 'normal',
  },
  headerContactSection: {
    width: 'auto',
    marginTop: 10,
    marginRight: 5,
    alignItems: 'flex-end',
  },
  headerContactText: {
    fontSize: 8,
    color: '#6D6E70',
    lineHeight: 1.25,
    textAlign: 'right',
  },
  headerContactBold: {
    fontWeight: 700,
  },
  headerLogoRight: {
    width: 80,
    height: 80,
    marginTop: 3,
    marginLeft: 8,
  },
  introSection: {
    marginTop: 10,
    marginBottom: 2,
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#D2D2D4',
    paddingHorizontal: 6,
    paddingTop: 3,
    paddingBottom: 4,
    /** Grow with commentary — do not lock to portrait height. */
    flexGrow: 0,
    flexShrink: 0,
  },
  introImageWrap: {
    alignSelf: 'flex-start',
  },
  introText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.2,
    textAlign: 'justify',
    color: '#000000',
    fontWeight: 600,
    paddingTop: 0,
  },
  /** Line inside Monatskommentar column — must not use flex or lines collapse to portrait height. */
  introTextLine: {
    fontSize: 9,
    lineHeight: 1.2,
    textAlign: 'justify',
    color: '#000000',
    fontWeight: 600,
  },
  introTextColumn: {
    flex: 1,
    flexDirection: 'column',
  },
  introTextLineWrap: {
    width: '100%',
  },
  twoColumnLayout: {
    flexDirection: 'row',
    marginTop: 8,
  },
  leftColumn: {
    width: 230,
    marginRight: 16,
  },
  /** Uniform vertical gap after each left-column factsheet block (STRATEGIE / PREISE / …) */
  leftColumnSection: {
    marginBottom: 8,
  },
  rightColumn: {
    flex: 1,
  },
  sectionTitle: {
    width: '100%',
    fontSize: 11,
    color: '#6D6E70',
    fontWeight: 900,
    marginBottom: 1,
    borderBottom: '0.5px solid #6d6e70',
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#a7a8ab',
    marginBottom: 2,
  },
  sectionText: {
    fontSize: 7.5,
    color: '#000000',
    lineHeight: 1.2,
    textAlign: 'justify',
    marginBottom: 8,
  },
  keyValueRow: {
    flexDirection: 'row',
    marginBottom: 0,
    lineHeight: 0.7,
  },
  /** Three-column row: label | share class 1 | share class 2 */
  keyValueTripleRow: {
    flexDirection: 'row',
    marginBottom: 0,
    width: '100%',
    alignItems: 'flex-start',
  },
  keyValueTripleLabel: {
    width: '36%',
    fontSize: 8,
    color: '#808080',
    paddingRight: 4,
  },
  keyValueTripleCell: {
    width: '32%',
    fontSize: 8,
    color: '#000000',
    paddingLeft: 2,
  },
  shareClassHeader: {
    width: '32%',
    fontSize: 8,
    color: '#808080',
    fontWeight: 700,
    paddingBottom: 2,
    paddingLeft: 2,
  },
  keyValueKey: {
    width: 90,
    fontSize: 8,
    color: '#808080',
  },
  keyValueValue: {
    flex: 1,
    fontSize: 8,
    color: '#000000',
    marginLeft: 3,
  },
  chartTitle: {
    fontSize: 10,
    color: '#6D6E70',
    fontWeight: 400,
    marginBottom: 6,
    borderBottom: '1px solid #6d6e70',
  },
  chartDivider: {
    height: 1.5,
    backgroundColor: '#a7a8ab',
    marginBottom: 4,
  },
  performanceTable: {
    marginTop: 4,
  },
  performanceTableHeader: {
    flexDirection: 'row',
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#a7a8ab',
  },
  performanceTableCell: {
    flex: 1,
    textAlign: 'center',
    fontSize: 7,
    fontWeight: 700,
    color: '#000000',
  },
  performanceTableFirstCell: {
    flex: 1,
    textAlign: 'left',
    fontSize: 7,
    fontWeight: 700,
    color: '#000000',
  },
  performanceTableRow: {
    flexDirection: 'row',
    marginTop: 2,
    gap: 2,
  },
  performanceTableValueCell: {
    flex: 1,
    backgroundColor: '#D2D2D4',
    paddingVertical: 1,
    paddingHorizontal: 4,
  },
  performanceTableValueText: {
    fontSize: 7,
    color: '#000000',
  },
  pieChartContainer: {
    marginTop: 15,
  },
  pieChartLegend: {
    width: 224,
    marginRight: 3,
  },
  pieChartImage: {
    width: 75,
    height: 75,
    marginLeft: 10,
  },
  pieChartLegendRow: {
    flexDirection: 'row',
    fontSize: 7.5,
    lineHeight: 1,
    marginBottom: 2.8,
    backgroundColor: '#D2D2D4',
    paddingTop: 2.8,
    paddingBottom: 2.8,
  },
  pieChartLegendLabel: {
    width: 170,
    textAlign: 'left',
  },
  pieChartLegendValue: {
    width: 30,
    textAlign: 'right',
  },
  pieChartLegendColor: {
    width: 5,
    height: 5,
    marginLeft: 5,
  },
  barChartContainer: {
    marginTop: 4,
    flexDirection: 'row',
  },
  barChartLegend: {
    width: 225,
  },
  barChartLegendRow: {
    flexDirection: 'row',
    fontSize: 7,
    lineHeight: 1.02,
    marginBottom: 4.8,
    backgroundColor: '#D2D2D4',
    paddingTop: 2.9,
    paddingBottom: 2.9,
  },
  barChartLegendLabel: {
    width: 179,
    textAlign: 'left',
  },
  barChartLegendValue: {
    width: 35,
    textAlign: 'right',
  },
  barChartImage: {
    width: 'auto',
    height: 'auto',
    marginLeft: 8,
    marginTop: 2.2,
  },
  disclaimer: {
    fontSize: 5.5,
    lineHeight: 1.2,
    textAlign: 'justify',
    marginTop: 16,
  },
  disclaimerTitle: {
    fontSize: 5.5,
    color: '#003F7A',
    fontWeight: 700,
  },
  footerNotes: {
    marginTop: 10,
    fontSize: 5,
    lineHeight: 1.5,
    color: '#000000',
    textAlign: 'justify',
  },
  // (business table styles are at the bottom of this file)
  contactRow: {
    flexDirection: 'row',
    marginTop: 20,
    marginLeft: 60,
    justifyContent: 'center',
  },
  contactCard: {
    width: 100,
    marginHorizontal: 10,
    alignItems: 'flex-start',
  },
  contactImage: {
    width: 100,
    height: 100,
  },
  contactName: {
    fontSize: 12,
    color: '#946A4E',
    fontWeight: 700,
    marginTop: 10,
  },
  contactRole: {
    fontSize: 9,
    color: '#444444',
    fontWeight: 700,
    marginTop: 3,
  },
  contactEmail: {
    fontSize: 9,
    color: '#444444',
    marginTop: 3,
  },
  contactTel: {
    fontSize: 9,
    color: '#444444',
    marginTop: 3,
  },
  socialMediaContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 5,
    gap: 15,
  },
  socialMediaIcon: {
    width: 25,
    height: 25,
  },
  // Business table styles
  businessTableContainer: {
    marginTop: 6,
  },
  businessTableTitleBar: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    marginBottom: 0,
  },
  businessTableTitleText: {
    fontSize: 8,
    fontWeight: 700,
    color: '#ffffff',
  },
  businessTableHeaderRow: {
    flexDirection: 'row' as const,
    borderBottom: '0.5px solid #e0e0e0',
  },
  businessTableHeaderCell: {
    paddingVertical: 2,
    paddingHorizontal: 3,
    fontSize: 8,
    fontWeight: 700,
    marginTop: 2.1,
  },
  businessTableBodyRow: {
    flexDirection: 'row' as const,
    borderBottom: '0.5px solid #e0e0e0',
    alignItems: 'center' as const,
  },
  businessTableBodyCell: {
    paddingHorizontal: 3,
    fontSize: 8,
    color: '#484848',
    fontWeight: 600,
    marginTop: 2.1,
  },
  businessTableIcon: {
    width: 15,
    height: 15,
    objectFit: 'contain' as const,
  },
});

/** Tighter page-1 layout for dual share-class template (6995ec804c4ced0db3932d1b) */
function getDualSharePdfStyles(): typeof pdfStyles {
  return {
    ...pdfStyles,
    page: {
      ...pdfStyles.page,
      padding: '14px 19px 14px 19px',
    },
    headerContainer: {
      ...pdfStyles.headerContainer,
      paddingBottom: 3,
    },
    introSection: {
      ...pdfStyles.introSection,
      marginTop: 8,
      marginBottom: 2,
      paddingTop: 3,
      paddingBottom: 4,
      paddingHorizontal: 6,
    },
    introText: {
      ...pdfStyles.introText,
      fontSize: 8.75,
      lineHeight: 1.18,
    },
    introTextLine: {
      ...pdfStyles.introTextLine,
      fontSize: 8.75,
      lineHeight: 1.18,
    },
    twoColumnLayout: {
      ...pdfStyles.twoColumnLayout,
      marginTop: 6,
    },
    leftColumnSection: {
      ...pdfStyles.leftColumnSection,
      marginBottom: 6,
    },
    sectionTitle: {
      ...pdfStyles.sectionTitle,
      marginBottom: 0.5,
      fontSize: 10.5,
    },
    sectionText: {
      ...pdfStyles.sectionText,
      lineHeight: 1.18,
      marginBottom: 6,
      fontSize: 7.4,
    },
    keyValueRow: {
      ...pdfStyles.keyValueRow,
      lineHeight: 0.68,
    },
    keyValueTripleRow: {
      ...pdfStyles.keyValueTripleRow,
      marginBottom: 0,
    },
    keyValueTripleLabel: {
      ...pdfStyles.keyValueTripleLabel,
      fontSize: 7.75,
    },
    keyValueTripleCell: {
      ...pdfStyles.keyValueTripleCell,
      fontSize: 7.75,
    },
    shareClassHeader: {
      ...pdfStyles.shareClassHeader,
      paddingBottom: 1.5,
      fontSize: 7.75,
    },
    keyValueKey: {
      ...pdfStyles.keyValueKey,
      fontSize: 7.75,
    },
    keyValueValue: {
      ...pdfStyles.keyValueValue,
      fontSize: 7.75,
    },
    chartTitle: {
      ...pdfStyles.chartTitle,
      marginBottom: 4,
      fontSize: 9.75,
    },
    page1ChartFootnotesInColumn: {
      ...pdfStyles.page1ChartFootnotesInColumn,
      paddingTop: 4,
    },
    performanceTableRow: {
      ...pdfStyles.performanceTableRow,
      marginTop: 1.5,
    },
  };
}

/** Tighter page-1 layout for Value Focus Fund (6995ed834c4ced0db3932d1c) */
function getValueFocusPdfStyles(): typeof pdfStyles {
  return {
    ...pdfStyles,
    page: {
      ...pdfStyles.page,
      padding: '14px 19px 14px 19px',
    },
    headerContainer: {
      ...pdfStyles.headerContainer,
      paddingBottom: 3,
    },
    introSection: {
      ...pdfStyles.introSection,
      marginTop: 7,
      marginBottom: 1.5,
      paddingTop: 2.5,
      paddingBottom: 3.5,
      paddingHorizontal: 6,
    },
    introText: {
      ...pdfStyles.introText,
      fontSize: 8.7,
      lineHeight: 1.16,
    },
    introTextLine: {
      ...pdfStyles.introTextLine,
      fontSize: 8.7,
      lineHeight: 1.16,
    },
    twoColumnLayout: {
      ...pdfStyles.twoColumnLayout,
      marginTop: 5,
    },
    leftColumnSection: {
      ...pdfStyles.leftColumnSection,
      marginBottom: 5,
    },
    sectionTitle: {
      ...pdfStyles.sectionTitle,
      marginBottom: 0.5,
      fontSize: 10.5,
    },
    sectionText: {
      ...pdfStyles.sectionText,
      lineHeight: 1.16,
      marginBottom: 5,
      fontSize: 7.35,
    },
    keyValueRow: {
      ...pdfStyles.keyValueRow,
      lineHeight: 0.66,
    },
    keyValueKey: {
      ...pdfStyles.keyValueKey,
      fontSize: 7.7,
    },
    keyValueValue: {
      ...pdfStyles.keyValueValue,
      fontSize: 7.7,
    },
    chartTitle: {
      ...pdfStyles.chartTitle,
      marginBottom: 3.5,
      fontSize: 9.75,
    },
    page1ChartFootnotesInColumn: {
      ...pdfStyles.page1ChartFootnotesInColumn,
      paddingTop: 3.5,
    },
    performanceTableRow: {
      ...pdfStyles.performanceTableRow,
      marginTop: 1.25,
    },
  };
}

export function getPvmGpsPdfStyles(): typeof pdfStyles {
  return {
    ...pdfStyles,
    page: {
      ...pdfStyles.page,
      padding: '12px 19px 6px 19px',
      fontFamily: PVM_BODY_FONT,
    },
    introSection: {
      ...pdfStyles.introSection,
      marginTop: 3,
      marginBottom: 0,
      paddingTop: 1.5,
      paddingBottom: 2,
      paddingHorizontal: 4,
    },
    introText: {
      ...pdfStyles.introText,
      fontFamily: PVM_BODY_FONT,
      color: PVM_HEADLINE_COLOR,
      fontSize: 7.9,
      lineHeight: 0.98,
    },
    introTextLine: {
      ...pdfStyles.introTextLine,
      fontFamily: PVM_BODY_FONT,
      color: PVM_HEADLINE_COLOR,
      fontSize: 7.9,
      lineHeight: 0.98,
    },
    twoColumnLayout: {
      ...pdfStyles.twoColumnLayout,
      marginTop: 2,
    },
    leftColumn: {
      ...pdfStyles.leftColumn,
      width: 195,
      marginRight: 14,
    },
    leftColumnSection: {
      ...pdfStyles.leftColumnSection,
      marginBottom: 2,
    },
    rightColumn: {
      ...pdfStyles.rightColumn,
      flex: 1,
    },
    sectionText: {
      ...pdfStyles.sectionText,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      fontSize: 6.7,
      lineHeight: 0.9,
      marginBottom: 0,
    },
    keyValueRow: {
      ...pdfStyles.keyValueRow,
      lineHeight: 0.52,
      marginBottom: 0,
    },
    keyValueKey: {
      ...pdfStyles.keyValueKey,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      fontSize: 6.7,
      width: '42%',
    },
    keyValueValue: {
      ...pdfStyles.keyValueValue,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      fontSize: 6.7,
      width: '58%',
      flex: 1,
      marginLeft: 0,
      lineHeight: 0.88,
    },
    headerSplitter: {
      ...pdfStyles.headerSplitter,
      width: '100%',
      backgroundColor: PVM_HEADLINE_COLOR,
    },
    headerContainer: {
      ...pdfStyles.headerContainer,
      alignItems: 'center',
      paddingTop: PVM_HEADER_PADDING_BELOW_TOP_BORDER_PDF,
    },
    headerLeftSection: {
      ...pdfStyles.headerLeftSection,
      alignItems: 'center',
    },
    headerTitleSection: {
      ...pdfStyles.headerTitleSection,
      marginTop: 0,
    },
    headerMainTitle: {
      ...pdfStyles.headerMainTitle,
      fontFamily: PVM_HEADLINE_FONT,
      fontWeight: 700,
      color: PVM_HEADLINE_COLOR,
      fontSize: PVM_HEADER_TITLE_FONT_SIZE_PDF,
      lineHeight: 1.15,
      width: 'auto',
      maxWidth: '100%',
    },
    headerTitleAddition: {
      ...pdfStyles.headerTitleAddition,
      fontSize: PVM_HEADER_TITLE_FONT_SIZE_PDF,
    },
    headerContactSection: {
      ...pdfStyles.headerContactSection,
      marginTop: 0,
      marginRight: 0,
      width: PVM_HEADER_RIGHT_COLUMN_WIDTH_PDF,
      alignItems: 'stretch',
    },
    headerContactText: {
      ...pdfStyles.headerContactText,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      textAlign: 'right',
      width: '100%',
      fontSize: 7.2,
      lineHeight: 1.05,
    },
    headerContactBold: {
      ...pdfStyles.headerContactBold,
      fontFamily: PVM_BODY_FONT,
      fontWeight: 700,
      color: PVM_TEXT_COLOR,
    },
    headerTitleText: {
      ...pdfStyles.headerTitleText,
      fontFamily: PVM_HEADLINE_FONT,
      fontWeight: 700,
      color: PVM_HEADLINE_COLOR,
    },
    headerDateText: {
      ...pdfStyles.headerDateText,
      fontFamily: PVM_BODY_FONT,
      fontWeight: 400,
      color: '#949599',
    },
    sectionTitle: {
      ...pdfStyles.sectionTitle,
      fontFamily: PVM_HEADLINE_FONT,
      fontWeight: 700,
      color: PVM_HEADLINE_COLOR,
      borderBottomColor: PVM_HEADLINE_COLOR,
      fontSize: 9,
      marginBottom: 0,
    },
    chartTitle: {
      ...pdfStyles.chartTitle,
      fontFamily: PVM_HEADLINE_FONT,
      fontWeight: 700,
      color: PVM_HEADLINE_COLOR,
      borderBottomColor: PVM_HEADLINE_COLOR,
      fontSize: 9,
      marginBottom: 3,
    },
    keyValueTripleLabel: {
      ...pdfStyles.keyValueTripleLabel,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      fontSize: 7,
    },
    keyValueTripleCell: {
      ...pdfStyles.keyValueTripleCell,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
      fontSize: 7,
    },
    footerNotes: {
      ...pdfStyles.footerNotes,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
    },
    disclaimer: {
      ...pdfStyles.disclaimer,
      fontSize: 5,
      lineHeight: 1.02,
      marginTop: 0,
      fontFamily: PVM_BODY_FONT,
      color: PVM_TEXT_COLOR,
    },
    disclaimerTitle: {
      ...pdfStyles.disclaimerTitle,
      fontSize: 5,
      fontFamily: PVM_BODY_FONT,
      color: PVM_HEADLINE_COLOR,
    },
  } as unknown as typeof pdfStyles;
}

export function getPdfStylesForTemplate(templateId?: string): typeof pdfStyles {
  if (isPvmGpsTemplate(templateId)) return getPvmGpsPdfStyles();
  if (isValueFocusCompactLayout(templateId)) return getValueFocusPdfStyles();
  if (isDualShareCompactLayout(templateId)) return getDualSharePdfStyles();
  return pdfStyles;
}
